import { alpha, SvgIcon, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import { UseMutationResult } from '@tanstack/react-query';

import { getFormattedDateString } from '@zeta-core';
import { ClockCircleBold, InfoIcon, WarningNavIcon } from '@zeta-core/assets';
import { IEmptyRequestResponse, IGenericErrorResponse } from '@zeta-types';
import { AxiosResponse } from 'axios';

// ----------------------------------------------------------------------

type NotificationItemProps = {
  notification: {
    id: string;
    title: string;
    appNotificationType: string;
    createdAt: Date;
    isUnRead: boolean;
    type: string;
  };
  markRead?: UseMutationResult<
    AxiosResponse<IEmptyRequestResponse, any>,
    IGenericErrorResponse,
    {
      notificationIDs: Array<string>;
      isReadAll: boolean;
    },
    unknown
  >;
};

export default function NotificationItem({ notification, markRead }: NotificationItemProps) {
  const theme = useTheme();

  const getBGColor = (type, section) => {
    switch (type) {
      case 'Info':
        if (section === 'icon') return alpha(theme.palette.info.main, 0.16);
        else if (section === 'badge') return theme.palette.info.main;
        return alpha(theme.palette.info.main, 0.08);
      case 'Reminder':
        if (section === 'icon') return alpha(theme.palette.warning.main, 0.16);
        else if (section === 'badge') return theme.palette.warning.main;
        return alpha(theme.palette.warning.main, 0.08);
      case 'Warning':
        if (section === 'icon') return alpha(theme.palette.error.main, 0.16);
        else if (section === 'badge') return theme.palette.error.main;
        return alpha(theme.palette.error.main, 0.08);
      default:
        if (section === 'icon') return alpha(theme.palette.info.main, 0.16);
        else if (section === 'badge') return theme.palette.info.main;
        return alpha(theme.palette.info.main, 0.08);
    }
  };

  const getNotiIcon = (type) => {
    switch (type) {
      case 'Info':
        return <InfoIcon color={`${theme.palette.info.dark}`} />;
      case 'Reminder':
        return (
          <SvgIcon height="8">
            <ClockCircleBold />
          </SvgIcon>
        );
      case 'Warning':
        return <WarningNavIcon />;
      default:
        return <InfoIcon color={`${theme.palette.info.dark}`} />;
    }
  };

  const renderAvatar = (
    <ListItemAvatar>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: getBGColor(notification.appNotificationType, 'icon'),
        }}
      >
        {getNotiIcon(notification.appNotificationType)}
      </Stack>
    </ListItemAvatar>
  );

  const renderText = (
    <ListItemText
      disableTypography
      sx={{
        color: 'text.primary',
      }}
      primary={reader(notification.message)}
      secondary={
        <Stack
          direction="row"
          alignItems="center"
          sx={{ typography: 'caption', color: 'text.disabled' }}
          justifyContent="space-between"
          divider={
            <Box
              sx={{
                width: 2,
                height: 2,
                bgcolor: 'currentColor',
                mx: 0.5,
                borderRadius: '50%',
              }}
            />
          }
        >
          {getFormattedDateString(notification.date, 'DD MMM YYYY')}
        </Stack>
      }
    />
  );

  const renderUnReadBadge = (
    <Box
      sx={{
        top: '50%',
        width: 8,
        height: 8,
        right: 20,
        borderRadius: '50%',
        bgcolor: getBGColor(notification.appNotificationType, 'badge'),
        position: 'absolute',
      }}
    />
  );

  return (
    <ListItemButton
      disableRipple
      sx={{
        p: 2.5,
        alignItems: 'flex-start',
        borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
        background: notification.notificationStatus === 'Unread' ? getBGColor(notification.appNotificationType, 'background') : theme.palette.common.white,
      }}
      onClick={() => {
        if (notification.notificationStatus === 'Unread') markRead.mutateAsync({ notificationIDs: [notification.id], isReadAll: false });
      }}
    >
      {notification.notificationStatus === 'Unread' && renderUnReadBadge}
      {renderAvatar}
      <Stack sx={{ flexGrow: 1 }}>{renderText}</Stack>
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function reader(data: string) {
  return (
    <Box
      dangerouslySetInnerHTML={{ __html: data }}
      sx={{
        mb: 0.5,
        '& p': { typography: 'body2', m: 0 },
        '& a': { color: 'inherit', textDecoration: 'none' },
        '& strong': { typography: 'subtitle2' },
      }}
    />
  );
}
